import { useSyncExternalStore } from "react";

/**
 * JUB-152: tracks autosave requests still in flight, per event uuid.
 *
 * A form that unmounts (tab switch, route change) flushes its last edits, and
 * that request can outlive the form. Anything that (re)loads server state for
 * the same event waits here first, so it never reads — and shows — values
 * older than an edit the user already typed.
 */
const pending = new Map<string, Set<Promise<unknown>>>();
const listeners = new Set<() => void>();

function emit() {
  for (const listener of listeners) listener();
}

export function trackPendingSave<T>(key: string, promise: Promise<T>): Promise<T> {
  let set = pending.get(key);
  if (!set) {
    set = new Set();
    pending.set(key, set);
  }
  const entry = promise.then(
    () => undefined,
    () => undefined,
  );
  set.add(entry);
  emit();
  entry.then(() => {
    const current = pending.get(key);
    current?.delete(entry);
    if (current && current.size === 0) pending.delete(key);
    emit();
  });
  return promise;
}

export function hasPendingSaves(key: string | undefined): boolean {
  return !!key && (pending.get(key)?.size ?? 0) > 0;
}

/** Resolves once no autosave for `key` is in flight (including ones queued meanwhile). */
export async function waitForPendingSaves(key: string | undefined): Promise<void> {
  while (key && hasPendingSaves(key)) {
    await Promise.all([...(pending.get(key) ?? [])]);
  }
}

function subscribe(listener: () => void) {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/** True while an autosave for `key` is still in flight. */
export function usePendingSaves(key: string | undefined): boolean {
  return useSyncExternalStore(subscribe, () => hasPendingSaves(key), () => false);
}

// ---------------------------------------------------------------- drafts

/**
 * Unsaved edits a form left behind (it unmounted before the server
 * acknowledged them: retries ran out, offline, refused). In memory only, so
 * they never outlive the page; scoped by account + event + form so they never
 * cross users, and cleared on logout. Kept until a save confirms them or the
 * user discards them.
 */
const drafts = new Map<string, Record<string, unknown>>();
/** Never retained, whatever a form sends (secrets, slug, publish state, media). */
const NEVER_RETAINED = new Set(["password", "url", "slug", "live", "gallery", "cover", "image"]);

export function draftKey(accountId: string, eventUuid: string, formId: string): string {
  return `${accountId}\u0000${eventUuid}\u0000${formId}`;
}

export function saveDraft(key: string, patch: Record<string, unknown>): void {
  const clean = Object.fromEntries(Object.entries(patch).filter(([k]) => !NEVER_RETAINED.has(k)));
  if (Object.keys(clean).length) drafts.set(key, clean);
  else drafts.delete(key);
}

export function getDraft(key: string): Record<string, unknown> | undefined {
  return drafts.get(key);
}

export function clearDraft(key: string): void {
  drafts.delete(key);
}

/**
 * Bumped on logout / login: a save still in flight from the previous session
 * must neither write a draft nor retry once it settles.
 */
let sessionEpoch = 0;

export function getSessionEpoch(): number {
  return sessionEpoch;
}

/** Logout / account switch: drop every retained draft and end the draft session. */
export function clearAllDrafts(): void {
  drafts.clear();
  sessionEpoch++;
}

/** Test helper: forget every tracked request and draft. */
export function resetPendingSavesForTests() {
  pending.clear();
  drafts.clear();
  emit();
}
